import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { PageHeader, RevealPasswordModal } from '../components/common';
import { ROUTES } from '../constants';
import { marketerService, ApiError } from '../services';

const inputClass =
  'w-full px-3 py-2 border border-gray-300 rounded-md text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500';

// IBAN must be transmitted in canonical form (no whitespace, uppercase) per
// ISO 13616. Banks/PDFs commonly display it grouped with spaces — strip
// silently so admins can paste from documents without breaking the value.
const sanitizeIban = value => String(value ?? '').replace(/\s+/g, '').toUpperCase();

const formDataFromMarketer = m => ({
  iban: sanitizeIban(m?.iban ?? m?.bankAccountNumber ?? ''),
  beneficiaryName: (m?.beneficiaryName ?? '').toString(),
});

const MarketerEditBankPage = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const marketerFromState = location.state?.marketer;

  // Header info can render immediately from route state; form values must
  // come from a fresh fetch so we know the `revealed` flag is current.
  const [marketer, setMarketer] = useState(marketerFromState || null);
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [revealed, setRevealed] = useState(false);
  const [revealModalOpen, setRevealModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [savedOk, setSavedOk] = useState(false);
  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const initialFetchDoneRef = useRef(false);
  // RevealPasswordModal calls onSuccess THEN onClose on a successful verify;
  // this ref tells onClose to skip the bounce-back redirect in that case.
  const justVerifiedRef = useRef(false);

  const detailHref = id
    ? ROUTES.generatePath(ROUTES.MARKETERS_DETAIL_LIST, { id })
    : ROUTES.MARKETERS;

  const goBackToDetail = useCallback(() => {
    if (!id) {
      navigate(ROUTES.MARKETERS);
      return;
    }
    navigate(detailHref, { state: marketer ? { marketer } : undefined });
  }, [detailHref, id, marketer, navigate]);

  const loadMarketer = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const response = await marketerService.getFormattedMarketerDetails(id);
      const data = response?.data;
      if (!data) {
        setError('Failed to load creator details.');
        return;
      }
      setMarketer(data);
      const isRevealed = data.revealed === true;
      setRevealed(isRevealed);
      if (isRevealed) {
        setForm(formDataFromMarketer(data));
      } else {
        // Don't pre-fill with masked values; wait for reveal then refetch.
        setForm({ iban: '', beneficiaryName: '' });
        setRevealModalOpen(true);
      }
    } catch (err) {
      console.error('Failed to load creator:', err);
      setError(
        err instanceof ApiError
          ? err.message
          : 'Failed to load creator. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (initialFetchDoneRef.current) return;
    initialFetchDoneRef.current = true;
    loadMarketer();
  }, [loadMarketer]);

  const handleChange = field => e => {
    const nextValue = e.target.value;
    setForm(prev => ({ ...prev, [field]: nextValue }));
    setFieldErrors(prev => {
      if (!prev?.[field]) return prev;
      const trimmed = String(nextValue ?? '').trim();
      if (!trimmed) return prev;
      const { [field]: _ignored, ...rest } = prev;
      return rest;
    });
    if (savedOk) setSavedOk(false);
  };

  const validate = currentForm => {
    const next = {};
    if (!String(currentForm.iban ?? '').trim()) {
      next.iban = 'IBAN is required.';
    }
    if (!String(currentForm.beneficiaryName ?? '').trim()) {
      next.beneficiaryName = 'Beneficiary Name is required.';
    }
    return next;
  };

  const handleSubmit = e => {
    e.preventDefault();
    if (!id || !form) return;
    const nextErrors = validate(form);
    if (Object.keys(nextErrors).length > 0) {
      setFieldErrors(nextErrors);
      setError('Please fill in all required fields.');
      return;
    }
    setError(null);
    setShowConfirm(true);
  };

  const handleConfirmSave = async () => {
    if (!id || !form) return;
    setShowConfirm(false);
    setSaving(true);
    setError(null);
    try {
      await marketerService.updateMarketerBank(id, {
        iban: form.iban,
        beneficiaryName: form.beneficiaryName,
      });
      setSavedOk(true);
      // Keep latest input values in form; refresh marketer cache so the
      // detail page (when user navigates back) sees the new values.
      setMarketer(prev =>
        prev
          ? {
              ...prev,
              iban: String(form.iban).trim(),
              beneficiaryName: String(form.beneficiaryName).trim(),
            }
          : prev
      );
    } catch (err) {
      console.error('Failed to save bank:', err);
      const code = err instanceof ApiError ? err.code : null;
      if (code === 'FeatureUnauthorized') {
        setError(
          'Your admin session has expired or lacks permission. Please re-verify and try again.'
        );
      } else if (code === 'FeatureInvalidPayload') {
        setError('IBAN or Beneficiary Name is invalid. Please review the values.');
      } else if (code === 'DatabaseNotFound') {
        setError('Creator not found.');
      } else {
        setError(
          err instanceof ApiError
            ? err.message
            : 'Failed to save bank details. Please try again.'
        );
      }
    } finally {
      setSaving(false);
    }
  };

  const marketerLabel =
    marketer?.fullName ||
    [marketer?.firstName, marketer?.lastName].filter(Boolean).join(' ') ||
    'Creator';

  if (loading && !marketer) {
    return (
      <div className='flex items-center justify-center min-h-[40vh]'>
        <div className='flex items-center space-x-2'>
          <svg
            className='animate-spin h-8 w-8 text-blue-600'
            fill='none'
            viewBox='0 0 24 24'
          >
            <circle
              className='opacity-25'
              cx='12'
              cy='12'
              r='10'
              stroke='currentColor'
              strokeWidth='4'
            />
            <path
              className='opacity-75'
              fill='currentColor'
              d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
            />
          </svg>
          <span className='text-gray-600'>Loading…</span>
        </div>
      </div>
    );
  }

  const hasFieldError = key => !!fieldErrors?.[key];
  const inputClassFor = key =>
    `${inputClass} ${
      hasFieldError(key)
        ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
        : ''
    }`;

  // Form is editable only after reveal succeeded.
  const editable = revealed && !saving;

  return (
    <div className='space-y-6'>
      <PageHeader
        title='Edit Bank'
        subtitle="Edit the creator's bank information and make sure the data is correct"
        breadcrumbs={[
          { name: 'Home', href: ROUTES.DASHBOARD },
          { name: 'Creators', href: ROUTES.MARKETERS },
          { name: marketerLabel, href: detailHref, state: { marketer } },
          { name: 'Edit Bank' },
        ]}
      />

      {savedOk && (
        <div className='bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 flex items-center justify-between gap-3'>
          <p className='text-sm text-amber-800'>Bank information saved.</p>
          <button
            type='button'
            onClick={goBackToDetail}
            className='px-3 py-1.5 border border-amber-300 rounded-md text-xs font-medium text-amber-800 bg-white hover:bg-amber-50 whitespace-nowrap'
          >
            Back to Details
          </button>
        </div>
      )}

      {error && (
        <div className='bg-red-50 border border-red-200 rounded-lg p-4'>
          <p className='text-sm text-red-700'>{error}</p>
        </div>
      )}

      <form
        onSubmit={handleSubmit}
        className='bg-white rounded-lg shadow-sm border border-gray-200'
      >
        <div className='px-4 md:px-6 py-5 border-b border-gray-200'>
          <h3 className='text-lg font-semibold text-gray-900'>
            {marketerLabel}
          </h3>
          <p className='text-xs text-gray-500 mt-0.5 break-all'>
            Creator ID: {marketer?.id || '-'}
          </p>
        </div>

        <div className='p-6 space-y-6'>
          <div className='grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5'>
            <div>
              <label
                htmlFor='iban'
                className='block text-sm font-medium text-gray-900 mb-1'
              >
                IBAN <span className='text-red-600'>*</span>
              </label>
              <input
                id='iban'
                type='text'
                value={form?.iban ?? ''}
                onChange={e => {
                  // Sanitize on every keystroke/paste so the field never
                  // shows spaces or lowercase; user pasting "IT60 X054..."
                  // sees the canonical form immediately.
                  const sanitized = sanitizeIban(e.target.value);
                  handleChange('iban')({ target: { value: sanitized } });
                }}
                disabled={!editable}
                className={inputClassFor('iban')}
                autoComplete='off'
                aria-invalid={hasFieldError('iban')}
              />
              {hasFieldError('iban') && (
                <p className='mt-1 text-xs text-red-600'>{fieldErrors.iban}</p>
              )}
            </div>
            <div>
              <label
                htmlFor='beneficiaryName'
                className='block text-sm font-medium text-gray-900 mb-1'
              >
                Beneficiary Name <span className='text-red-600'>*</span>
              </label>
              <input
                id='beneficiaryName'
                type='text'
                value={form?.beneficiaryName ?? ''}
                onChange={handleChange('beneficiaryName')}
                disabled={!editable}
                className={inputClassFor('beneficiaryName')}
                autoComplete='name'
                aria-invalid={hasFieldError('beneficiaryName')}
              />
              {hasFieldError('beneficiaryName') && (
                <p className='mt-1 text-xs text-red-600'>
                  {fieldErrors.beneficiaryName}
                </p>
              )}
            </div>
          </div>

          <div className='flex flex-wrap items-center justify-end gap-3 pt-2'>
            <button
              type='button'
              onClick={goBackToDetail}
              disabled={saving}
              className='px-5 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50'
            >
              Cancel
            </button>
            <button
              type='submit'
              disabled={!editable}
              className='px-5 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed'
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </form>

      {showConfirm && (
        <div className='fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4'>
          <div className='bg-white rounded-lg shadow-xl w-full max-w-md p-6'>
            <h2 className='text-base font-semibold text-gray-900 mb-3'>
              Confirm Bank Update
            </h2>
            <p className='text-sm text-gray-600 mb-6'>
              Please confirm the update to the creator's bank information. Under
              GDPR, you are responsible for ensuring this data is accurate and
              up to date. This change will be logged with your user ID and a
              timestamp.
            </p>
            <div className='flex justify-end gap-3'>
              <button
                type='button'
                onClick={() => setShowConfirm(false)}
                className='px-5 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50'
              >
                Cancel
              </button>
              <button
                type='button'
                onClick={handleConfirmSave}
                className='px-5 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700'
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      <RevealPasswordModal
        isOpen={revealModalOpen}
        onClose={() => {
          setRevealModalOpen(false);
          if (justVerifiedRef.current) {
            justVerifiedRef.current = false;
            return;
          }
          if (!revealed) goBackToDetail();
        }}
        onSuccess={async () => {
          justVerifiedRef.current = true;
          await loadMarketer();
        }}
      />
    </div>
  );
};

export default MarketerEditBankPage;
